import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Logo } from '@/components/brand/Logo'

export function AuthVerifyPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [status, setStatus] = useState<'verifying' | 'error'>('verifying')
  const [errorMsg, setErrorMsg] = useState('')

  useEffect(() => {
    const token = searchParams.get('token')
    if (!token) {
      setStatus('error')
      setErrorMsg('Missing token. Please request a new link.')
      return
    }

    fetch('/api/auth/magic-link/verify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    })
      .then(r => r.json())
      .then((raw: unknown) => {
        const data = raw as { ok: boolean; token?: string; is_new_tenant?: boolean; intent?: string; error?: string };
        if (data.ok && data.token) {
          localStorage.setItem('echo_session', data.token)
          // Also set a same-site cookie so page.request (Playwright test API) can pick it up
          // The cookie is sent automatically in XHR/fetch from the same origin.
          document.cookie = `echo_session=${data.token}; path=/; SameSite=Lax; max-age=2592000`
          if (data.is_new_tenant || data.intent === 'signup') {
            navigate('/app/onboarding', { replace: true })
          } else {
            navigate('/app/demo', { replace: true })
          }
        } else {
          setStatus('error')
          setErrorMsg(data.error ?? 'Link expired. Please request a new one.')
        }
      })
      .catch(() => {
        setStatus('error')
        setErrorMsg('Could not verify. Please try again.')
      })
  }, [searchParams, navigate])

  return (
    <div className="min-h-screen bg-paper flex flex-col items-center justify-center px-4">
      <Logo size="md" className="mb-8" />
      {status === 'verifying' ? (
        <div className="text-center space-y-3">
          <div className="w-8 h-8 border-2 border-echo-mark border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-slate-500 text-sm">Verifying your link…</p>
        </div>
      ) : (
        <div className="text-center space-y-4 max-w-sm">
          <div className="w-12 h-12 bg-risk-rose/10 rounded-full flex items-center justify-center mx-auto">
            <span className="text-risk-rose text-xl">✕</span>
          </div>
          <h1 className="text-xl font-medium text-deep-ink">Link invalid or expired</h1>
          <p className="text-slate-500 text-sm">{errorMsg}</p>
          <a href="/signup" className="text-sm text-echo-mark hover:underline font-medium">
            Request a new link
          </a>
        </div>
      )}
    </div>
  )
}
